import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-dark text-white">
      {/* Header */}
      <div className="bg-dark-paper border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-gold" />
            <h1 className="text-2xl md:text-3xl font-serif text-gold">Politique de Confidentialité</h1>
          </div>
          <p className="text-gray-400 mt-2">Dernière mise à jour : mars 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-invert prose-gold max-w-none">
          
          {/* Introduction */}
          <div className="glass-card p-6 mb-6">
            <p className="text-gray-300 leading-relaxed">
              La présente politique de confidentialité (ci-après la « Politique ») a pour objectif de vous informer de manière claire et transparente sur la manière dont le site discreetlux.com (ci-après le « Site ») collecte, utilise et protège vos données personnelles. Nous accordons une importance particulière au respect de votre vie privée et nous engageons à traiter vos données conformément au Règlement (UE) 2016/679 (RGPD) et à la législation française applicable.
            </p>
          </div>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">1</span>
              Qui sommes-nous ?
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Le Site est édité par :</p>
              <div className="bg-dark-subtle p-4 rounded-lg">
                <p className="font-semibold text-white">NextDev</p>
                <p className="text-gray-400">Siège social : 142 rue de Rivoli 75001 Paris</p>
                <p className="text-gray-400 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> admin@discreetlux.com
                </p>
              </div>
              <p className="text-gray-300 mt-4">Le responsable du traitement est NextDev.</p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">2</span>
              Quelles données collectons-nous ?
            </h2>
            <div className="glass-card p-6 space-y-4">
              <p className="text-gray-300">Nous collectons différentes catégories de données personnelles selon votre utilisation du Site :</p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-white font-medium mb-2">a) Données fournies lors de l'inscription</h4>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Adresse email</li>
                    <li>Pseudonyme</li>
                    <li>Mot de passe</li>
                    <li>Date de naissance</li>
                    <li>Genre</li>
                    <li>Préférences personnelles (incluant potentiellement orientation ou centres d'intérêt)</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">b) Données de profil</h4>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Photos</li>
                    <li>Description personnelle</li>
                    <li>Localisation (ville ou région)</li>
                    <li>Informations physiques ou préférences</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">c) Données liées à l'utilisation</h4>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Messages, interactions, contenus publiés</li>
                    <li>Participation à événements ou services</li>
                    <li>Historique d'activité</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">d) Données techniques</h4>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Adresse IP</li>
                    <li>Type d'appareil et navigateur</li>
                    <li>Cookies et traceurs</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">e) Données de paiement (le cas échéant)</h4>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>Informations de transaction (hors données bancaires complètes si externalisées)</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">3</span>
              Données sensibles
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Certaines données peuvent être considérées comme sensibles (ex : vie sexuelle ou préférences). Ces données sont :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1 mb-4">
                <li>collectées uniquement avec votre consentement explicite</li>
                <li>strictement nécessaires au fonctionnement du service</li>
              </ul>
              <p className="text-gray-300">Vous pouvez retirer votre consentement à tout moment, mais cela peut limiter l'accès à certaines fonctionnalités.</p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">4</span>
              Finalités du traitement
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Vos données sont utilisées pour :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1">
                <li>Création et gestion de votre compte</li>
                <li>Fourniture des services (mise en relation, communication, fonctionnalités)</li>
                <li>Gestion des abonnements</li>
                <li>Personnalisation de l'expérience utilisateur</li>
                <li>Sécurité du Site et prévention des fraudes</li>
                <li>Amélioration du service (statistiques, analyses)</li>
                <li>Envoi de communications (emails, notifications)</li>
                <li>Respect des obligations légales</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">5</span>
              Base légale des traitements
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Nous traitons vos données sur les bases suivantes :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1">
                <li>Consentement (notamment pour données sensibles et marketing)</li>
                <li>Exécution du contrat (fourniture des services)</li>
                <li>Intérêt légitime (sécurité, amélioration du service)</li>
                <li>Obligations légales</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">6</span>
              Destinataires des données
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Vos données peuvent être transmises à :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1 mb-4">
                <li>Nos équipes internes autorisées</li>
                <li>Nos prestataires techniques (hébergement, paiement, support)</li>
                <li>Les autorités légales lorsque requis</li>
              </ul>
              <p className="text-gold font-medium">Les données ne sont jamais vendues à des tiers.</p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">7</span>
              Transferts hors Union Européenne
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Dans certains cas, vos données peuvent être transférées hors UE. Nous garantissons que ces transferts sont encadrés par :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1">
                <li>des clauses contractuelles types ou</li>
                <li>toute autre garantie conforme au RGPD</li>
              </ul>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">8</span>
              Durée de conservation
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Vos données sont conservées :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1 mb-4">
                <li>Pendant la durée d'utilisation du compte</li>
                <li>Puis archivées selon les obligations légales</li>
              </ul>
              <p className="text-gray-300 mb-2">Exemples :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1 mb-4">
                <li>Compte actif : durée d'utilisation</li>
                <li>Données de facturation : jusqu'à 5 ans</li>
                <li>Données techniques : jusqu'à 12 mois</li>
              </ul>
              <p className="text-gray-300">Les comptes inactifs peuvent être supprimés après une période définie.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">9</span>
              Sécurité des données
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Nous mettons en oeuvre des mesures techniques et organisationnelles adaptées pour protéger vos données :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1">
                <li>Chiffrement</li>
                <li>Contrôle des accès</li>
                <li>Sécurisation des serveurs</li>
                <li>Politique interne de confidentialité</li>
              </ul>
            </div>
          </section>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">10</span>
              Cookies
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Le Site utilise des cookies pour :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1 mb-4">
                <li>assurer son bon fonctionnement</li>
                <li>analyser l'audience</li>
                <li>améliorer l'expérience utilisateur</li>
              </ul>
              <p className="text-gray-300">Vous pouvez gérer vos préférences via le bandeau cookies.</p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">11</span>
              Vos droits
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside text-gray-400 space-y-1 mb-4">
                <li>Droit d'accès</li>
                <li>Droit de rectification</li>
                <li>Droit d'effacement</li>
                <li>Droit d'opposition</li>
                <li>Droit à la limitation</li>
                <li>Droit à la portabilité</li>
              </ul>
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-4">
                <p className="text-gray-300">Vous pouvez exercer vos droits en contactant :</p>
                <p className="text-gold font-medium flex items-center gap-2 mt-2">
                  <Mail className="w-4 h-4" /> admin@discreetlux.com
                </p>
              </div>
              <p className="text-gray-300 mt-4">Vous avez également le droit de saisir la CNIL.</p>
            </div>
          </section>

          {/* Section 12 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">12</span>
              Gestion des réclamations
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300">Toute demande ou réclamation relative à vos données sera traitée dans les meilleurs délais.</p>
            </div>
          </section>

          {/* Section 13 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">13</span>
              Modification de la politique
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300">Nous pouvons modifier la présente Politique à tout moment. En cas de modification substantielle, vous serez informé.</p>
            </div>
          </section>

          {/* Section 14 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">14</span>
              Conditions d'âge
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300 font-medium">L'accès au Site est strictement réservé aux personnes majeures (18 ans ou plus).</p>
            </div>
          </section>

          {/* Section 15 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm">15</span>
              Liens vers des sites tiers
            </h2>
            <div className="glass-card p-6">
              <p className="text-gray-300">Le Site peut contenir des liens vers des services externes. Nous ne sommes pas responsables de leurs politiques de confidentialité.</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
